// Conditional approvals: only the rows that apply to this package (Standard offer: HM + HRBP; Sign-on or Relocation:
// the organizational leader, obtained by the HM; VP+ LTI: Total Rewards), the leader and Total Rewards rows first.
// The approver's controls sit under the one actionable row. The Hiring Manager sees approval evidence only for
// their own acts, tagged Own-action only.
import { useState, type ReactNode } from "react";
import { ApprovalRow, Approvals, Card, COMMON_LABELS, EmptyState, G6_LABELS, ROLE_LABELS, Tag, VERSION_TEMPLATES } from "@/mc";
import type { ApprovalKey, ApprovalState, CompApproval, CompOut, DecideInput } from "./CompensationTab";
import { applicable } from "./CompensationTab";
import { ApproverControls } from "./ApproverControls";
import { EvidenceAttach, leaderComplete, type EvidenceValue } from "./EvidenceAttach";
import { APPROVAL_STATE_LABELS, PKG_LABELS, PKG_RULES } from "./labels";
import { TotalRewardsForm, totalRewardsComplete, type TotalRewardsValue } from "./TotalRewardsForm";

export const ROLE_WORD: Record<ApprovalKey, string> = {
  org_leader: G6_LABELS.organizationalLeader,
  total_rewards: G6_LABELS.totalRewards,
  hm: ROLE_LABELS.hmAbbr,
  hrbp: ROLE_LABELS.hrbpAbbr,
};

export const STATE_WORD: Record<ApprovalState, string> = {
  pending: APPROVAL_STATE_LABELS.pending,
  approved: APPROVAL_STATE_LABELS.approved,
  rejected: APPROVAL_STATE_LABELS.rejected,
  revision_required: APPROVAL_STATE_LABELS.revisionRequired,
};

const evidenceHref = (row: CompApproval): string | undefined =>
  row.evidence?.file_id ? `/api/files/${encodeURIComponent(row.evidence.file_id)}` : undefined;

export interface ApprovalsPanelProps {
  comp: CompOut;
  limited: boolean;
  actionable: ApprovalKey | null;
  onDecide?: ((input: DecideInput) => Promise<unknown>) | undefined;
  onUpload?: ((file: File) => Promise<string>) | undefined;
  timeZone?: string | undefined;
}

function ActionRow({ kind, onDecide, onUpload }: { kind: ApprovalKey; onDecide: ApprovalsPanelProps["onDecide"]; onUpload: ApprovalsPanelProps["onUpload"] }) {
  const [evidence, setEvidence] = useState<EvidenceValue>({});
  const [tr, setTr] = useState<TotalRewardsValue>({ amount: "", wording: "" });
  if (kind === "org_leader") {
    return (
      <ApproverControls
        kind={kind}
        onDecide={onDecide}
        approveOnly
        approveBlocked={leaderComplete(evidence) ? undefined : { reason: `${G6_LABELS.evidence} · ${COMMON_LABELS.required}` }}
        extra={() => ({ approver_name: evidence.name?.trim() ?? "", evidence: { ...(evidence.file_id ? { file_id: evidence.file_id } : {}), ...(evidence.ref?.trim() ? { ref: evidence.ref.trim() } : {}) } })}
      >
        <EvidenceAttach value={evidence} onChange={setEvidence} onUpload={onUpload} />
      </ApproverControls>
    );
  }
  if (kind === "total_rewards") {
    return (
      <ApproverControls
        kind={kind}
        onDecide={onDecide}
        approveOnly
        approveBlocked={totalRewardsComplete(tr) ? undefined : { reason: COMMON_LABELS.required }}
        extra={() => ({ approved_amount: tr.amount.trim(), approved_wording: tr.wording.trim() })}
      >
        <TotalRewardsForm value={tr} onChange={setTr} />
      </ApproverControls>
    );
  }
  return <ApproverControls kind={kind} onDecide={onDecide} />;
}

export function ApprovalsPanel({ comp, limited, actionable, onDecide, onUpload }: ApprovalsPanelProps) {
  const rows = applicable(comp);
  const pkg = comp.package ?? null;
  const items: ReactNode[] = [];
  for (const row of rows) {
    // The HM reads evidence only for their own acts (row 6: "No or own-action only").
    const showEvidence = !limited || row.own === true;
    items.push(
      <ApprovalRow
        key={row.requirement}
        role={ROLE_WORD[row.requirement]}
        person={{ name: row.approver_name ?? row.person ?? "—", hueKey: row.person ?? undefined }}
        state={STATE_WORD[row.state]}
        version={row.version ? VERSION_TEMPLATES.version({ n: row.version }) : pkg ? VERSION_TEMPLATES.version({ n: pkg.version }) : undefined}
        evidence={showEvidence ? evidenceHref(row) : undefined}
        obtainedBy={row.requirement === "org_leader" ? ROLE_LABELS.hmAbbr : undefined}
        hs={row.requirement === "org_leader" ? "offer.pkg.evidence.link" : undefined}
      />,
    );
    if (limited && row.own === true && row.evidence) {
      items.push(
        <li key={`${row.requirement}-own`} className="mc-pkg-own" data-own={row.requirement}>
          <Tag tone="limited">{PKG_LABELS.ownAction}</Tag>
          {row.evidence.ref && !row.evidence.file_id ? <span className="mc-mono">{row.evidence.ref}</span> : null}
        </li>,
      );
    }
    if (!limited && row.evidence?.ref && !row.evidence.file_id) {
      items.push(<li key={`${row.requirement}-ref`} className="mc-pkg-ref"><span className="mc-mono">{row.evidence.ref}</span></li>);
    }
    if (row.note && row.state !== "approved") {
      items.push(<li key={`${row.requirement}-note`} className="mc-pkg-note">{row.note}</li>);
    }
    if (row.requirement === actionable) {
      items.push(
        <li key={`${row.requirement}-act`} className="mc-pkg-act" data-actionable={row.requirement}>
          <ActionRow kind={row.requirement} onDecide={onDecide} onUpload={onUpload} />
        </li>,
      );
    }
  }
  return (
    <Card title={PKG_LABELS.conditionalApprovals} icon="approvals"  count={rows.length || undefined}>
      {rows.length === 0 ? (
        <EmptyState inline>{PKG_RULES.standard}</EmptyState>
      ) : (
        <>
          <Approvals label={PKG_LABELS.conditionalApprovals}>{items}</Approvals>
          <p className="mc-hint">{PKG_RULES.invalidates}</p>
        </>
      )}
    </Card>
  );
}
